import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Paper, Stack, Grid, CircularProgress, Alert, Chip, Divider
} from '@mui/material';
import { Clock, Utensils, Apple, Coffee, LogIn, LogOut, Activity, Languages, Calendar } from 'lucide-react';
import { getRetreatProgram } from '../../../Services/Retreats/RetreatApis';

// ─── Shared primitives (same design language as all other tabs) ───────────────

const SectionTitle = ({ icon: Icon, children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Icon size={15} color="#FFBD65" strokeWidth={2.2} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', letterSpacing: '0.02em' }}>
            {children}
        </Typography>
    </Box>
);

const InfoRow = ({ label, children, noBorder }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'flex-start',
            py: 0.9,
            borderBottom: noBorder ? 'none' : '1px solid #F1F5F9',
            gap: { xs: 0.25, sm: 0 },
        }}
    >
        <Typography
            variant="caption"
            sx={{ color: '#94A3B8', fontWeight: 500, minWidth: { sm: 150 }, pt: { sm: '2px' }, flexShrink: 0 }}
        >
            {label}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            {typeof children === 'string' || typeof children === 'number' ? (
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>{children}</Typography>
            ) : children}
        </Box>
    </Box>
);

const InfoCard = ({ children, sx }) => (
    <Paper
        elevation={0}
        sx={{ border: '1px solid #E8EEF4', borderRadius: '10px', bgcolor: '#FFFFFF', overflow: 'hidden', ...sx }}
    >
        {children}
    </Paper>
);

/** Format time from "06:00:00" or "19:00" to "6:00 AM" / "7:00 PM" */
const formatTime = (timeStr) => {
    if (!timeStr) return '—';
    const parts = timeStr.toString().trim().split(':');
    if (parts.length >= 2) {
        let hours = parseInt(parts[0], 10);
        const minutes = parts[1];
        if (!isNaN(hours)) {
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            return `${displayHours}:${minutes} ${ampm}`;
        }
    }
    return timeStr;
};

/** Inline chip group for meals / drinks / diet */
const ChipGroup = ({ items, labelKey, fallbackKey, icon: Icon, chipColor }) => {
    if (!items?.length) return null;
    return (
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {items.map((item, idx) => {
                const label = item[labelKey] || item[fallbackKey] || item.name || item.title || item.label || '—';
                return (
                    <Chip
                        key={idx}
                        label={label}
                        size="small"
                        icon={Icon ? <Icon size={12} color={chipColor || '#475569'} /> : undefined}
                        sx={{
                            height: 24,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            bgcolor: '#F8FAFC',
                            color: '#334155',
                            border: '1px solid #E2E8F0',
                            borderRadius: '6px',
                        }}
                    />
                );
            })}
        </Stack>
    );
};

const cardPx = { xs: 2, sm: 2.5 };

// ─── Main component ───────────────────────────────────────────────────────────

const FoodProgramTab = ({ retreat }) => {
    const [retreatProgram, setRetreatProgram] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!retreat?.id) return;
            try {
                setLoading(true);
                const response = await getRetreatProgram(retreat.id);
                if (response?.success) {
                    setRetreatProgram(response.data);
                } else {
                    setError('Failed to fetch program information.');
                }
            } catch (err) {
                console.error('Error fetching retreat program:', err);
                setError('An error occurred while fetching program information.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [retreat?.id]);

    // Data extraction with fallbacks to cover all API response structures
    const program = retreatProgram?.retreatProgram || retreatProgram;
    const meals = retreatProgram?.meals || program?.meals || [];
    const drinks = retreatProgram?.drinks || program?.drinks || [];
    const dietOptions = retreatProgram?.dietOptions || program?.dietOptions || [];
    const dailyActivities = useMemo(() => {
        const prog = retreatProgram?.retreatProgram || retreatProgram;
        return retreatProgram?.dailyActivities || prog?.dailyActivities || [];
    }, [retreatProgram]);
    const instructionLanguages = retreatProgram?.instructionLanguages || program?.instructionLanguages || [];

    // Group activities by day if day numbers/names exist
    const groupedActivities = useMemo(() => {
        if (!dailyActivities || dailyActivities.length === 0) return {};
        const groups = {};
        dailyActivities.forEach((act) => {
            let dayKey = 'Daily Schedule';
            if (act.day !== null && act.day !== undefined && act.day !== '') {
                dayKey = typeof act.day === 'number' ? `Day ${act.day}` : act.day.toString();
            }
            if (!groups[dayKey]) groups[dayKey] = [];
            groups[dayKey].push(act);
        });

        // Sort keys chronologically (e.g. Day 1, Day 2, Day 3)
        const sortedGroups = {};
        Object.keys(groups)
            .sort((a, b) => {
                const numA = parseInt(a.replace(/\D/g, ''), 10);
                const numB = parseInt(b.replace(/\D/g, ''), 10);
                if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                return a.localeCompare(b);
            })
            .forEach((k) => {
                sortedGroups[k] = groups[k];
            });

        return sortedGroups;
    }, [dailyActivities]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={28} sx={{ color: '#FFBD65' }} />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ borderRadius: '8px' }}>{error}</Alert>;
    }

    if (!retreatProgram) {
        return (
            <InfoCard sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No program information available.</Typography>
            </InfoCard>
        );
    }

    const hasFoodSection =
        Boolean(program?.foodDescription) ||
        meals.length > 0 ||
        drinks.length > 0 ||
        dietOptions.length > 0;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* ── 1. Program Schedule ───────────────────────────────────────── */}
            {program && (
                <Box>
                    <SectionTitle icon={Clock}>Program Schedule</SectionTitle>
                    <InfoCard>
                        {/* Check-in / Check-out strip */}
                        {(program.checkInTime || program.checkOutTime) && (
                            <Grid container sx={{ borderBottom: '1px solid #F1F5F9' }}>
                                <Grid
                                    size={{ xs: 6 }}
                                    sx={{
                                        px: cardPx, py: 1.5,
                                        borderRight: '1px solid #F1F5F9',
                                        display: 'flex', alignItems: 'center', gap: 1.25,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 32, height: 32, borderRadius: '8px',
                                            bgcolor: '#F0FDF4', border: '1px solid #BBF7D0',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                        }}
                                    >
                                        <LogIn size={14} color="#16A34A" />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 500, display: 'block' }}>
                                            Check-in
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                            {formatTime(program.checkInTime)}
                                            {program.checkInTime && program.checkInTime.includes(':') && (
                                                <Typography component="span" sx={{ color: '#94A3B8', fontSize: '0.75rem', fontWeight: 400, ml: 0.5 }}>
                                                    ({program.checkInTime.slice(0, 5)})
                                                </Typography>
                                            )}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid
                                    size={{ xs: 6 }}
                                    sx={{
                                        px: cardPx, py: 1.5,
                                        display: 'flex', alignItems: 'center', gap: 1.25,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 32, height: 32, borderRadius: '8px',
                                            bgcolor: '#FFF7ED', border: '1px solid #FED7AA',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                        }}
                                    >
                                        <LogOut size={14} color="#FFBD65" />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 500, display: 'block' }}>
                                            Check-out
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                            {formatTime(program.checkOutTime)}
                                            {program.checkOutTime && program.checkOutTime.includes(':') && (
                                                <Typography component="span" sx={{ color: '#94A3B8', fontSize: '0.75rem', fontWeight: 400, ml: 0.5 }}>
                                                    ({program.checkOutTime.slice(0, 5)})
                                                </Typography>
                                            )}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}

                        {/* Program Attributes & Instruction Languages */}
                        <Box sx={{ px: cardPx, py: 1 }}>
                            {program.isSameEveryDay !== undefined && (
                                <InfoRow label="Schedule Type">
                                    <Chip
                                        icon={<Calendar size={12} color="#475569" />}
                                        label={program.isSameEveryDay ? "Same schedule every day" : "Different schedule each day"}
                                        size="small"
                                        sx={{
                                            height: 24,
                                            fontSize: '0.75rem',
                                            fontWeight: 500,
                                            bgcolor: '#F8FAFC',
                                            color: '#334155',
                                            border: '1px solid #E2E8F0',
                                            borderRadius: '6px'
                                        }}
                                    />
                                </InfoRow>
                            )}

                            {program.hasSpecificTimes !== undefined && (
                                <InfoRow label="Timing Schedule">
                                    <Chip
                                        icon={<Clock size={12} color="#475569" />}
                                        label={program.hasSpecificTimes ? "Specific times scheduled" : "Flexible timing"}
                                        size="small"
                                        sx={{
                                            height: 24,
                                            fontSize: '0.75rem',
                                            fontWeight: 500,
                                            bgcolor: '#F8FAFC',
                                            color: '#334155',
                                            border: '1px solid #E2E8F0',
                                            borderRadius: '6px'
                                        }}
                                    />
                                </InfoRow>
                            )}

                            {instructionLanguages.length > 0 && (
                                <InfoRow label="Instruction Language" noBorder={!program.description}>
                                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                                        {instructionLanguages.map((langItem, idx) => {
                                            const langName = langItem.language?.name || langItem.languageName || langItem.name || '—';
                                            return (
                                                <Chip
                                                    key={idx}
                                                    icon={<Languages size={12} color="#2563EB" />}
                                                    label={langName}
                                                    size="small"
                                                    sx={{
                                                        height: 24,
                                                        fontSize: '0.75rem',
                                                        fontWeight: 500,
                                                        bgcolor: '#EFF6FF',
                                                        color: '#1E40AF',
                                                        border: '1px solid #BFDBFE',
                                                        borderRadius: '6px',
                                                    }}
                                                />
                                            );
                                        })}
                                    </Stack>
                                </InfoRow>
                            )}
                        </Box>

                        {/* Description */}
                        {program.description && (
                            <Box sx={{ px: cardPx, py: 1.5, borderTop: '1px solid #F1F5F9' }}>
                                <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1 }}>
                                    Program Description
                                </Typography>
                                <Box
                                    sx={{
                                        color: '#475569', fontSize: '0.85rem', lineHeight: 1.65,
                                        '& p': { mb: 1 },
                                        '& ul, & ol': { pl: '1.2rem', mb: 1, '& li': { mb: 0.5 } },
                                        '& *': {
                                            fontFamily: 'inherit !important',
                                            fontSize: 'inherit !important',
                                            color: 'inherit !important',
                                            backgroundColor: 'transparent !important'
                                        },
                                    }}
                                    dangerouslySetInnerHTML={{ __html: program.description }}
                                />
                            </Box>
                        )}
                    </InfoCard>
                </Box>
            )}

            {/* ── 2. Daily Activities & Schedule ────────────────────────────── */}
            {dailyActivities.length > 0 && (
                <Box>
                    <SectionTitle icon={Activity}>Daily Activities & Schedule</SectionTitle>
                    <InfoCard>
                        <Box sx={{ p: cardPx, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {Object.entries(groupedActivities).map(([dayTitle, activities], groupIdx, arr) => (
                                <Box
                                    key={groupIdx}
                                    sx={{
                                        pb: groupIdx < arr.length - 1 ? 2.5 : 0,
                                        borderBottom: groupIdx < arr.length - 1 ? '1px solid #F1F5F9' : 'none',
                                    }}
                                >
                                    {arr.length > 1 && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#FFBD65' }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                                {dayTitle}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 500 }}>
                                                ({activities.length} {activities.length === 1 ? 'activity' : 'activities'})
                                            </Typography>
                                        </Box>
                                    )}
                                    <Grid container spacing={1.5}>
                                        {activities.map((act, actIdx) => (
                                            <Grid
                                                size={{
                                                    xs: 12,
                                                    sm: 6,
                                                    md: activities.length <= 2 ? 6 : 4,
                                                }}
                                                key={act.id || actIdx}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.5,
                                                        p: 1.25,
                                                        px: 1.5,
                                                        borderRadius: '8px',
                                                        bgcolor: '#F8FAFC',
                                                        border: '1px solid #EEF2F6',
                                                        height: '100%',
                                                        boxSizing: 'border-box',
                                                        transition: 'all 0.15s ease-in-out',
                                                        '&:hover': {
                                                            bgcolor: '#F1F5F9',
                                                            borderColor: '#E2E8F0',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                                        }
                                                    }}
                                                >
                                                    {act.activityTime ? (
                                                        <Chip
                                                            icon={<Clock size={12} color="#D97706" />}
                                                            label={formatTime(act.activityTime)}
                                                            size="small"
                                                            sx={{
                                                                fontWeight: 600,
                                                                fontSize: '0.75rem',
                                                                bgcolor: '#FEF3C7',
                                                                color: '#B45309',
                                                                border: '1px solid #FDE68A',
                                                                borderRadius: '6px',
                                                                height: 24,
                                                                flexShrink: 0
                                                            }}
                                                        />
                                                    ) : (
                                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#FFBD65', flexShrink: 0, ml: 0.5 }} />
                                                    )}
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: '#1E293B',
                                                                textTransform: 'capitalize',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                            title={act.activityTitle || 'Untitled Activity'}
                                                        >
                                                            {act.activityTitle || 'Untitled Activity'}
                                                        </Typography>
                                                        {act.description && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: '#64748B',
                                                                    display: 'block',
                                                                    mt: 0.25,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                                title={act.description}
                                                            >
                                                                {act.description}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            ))}
                        </Box>
                    </InfoCard>
                </Box>
            )}

            {/* ── 3. Food & Dining ──────────────────────────────────────────── */}
            {hasFoodSection && (
                <Box>
                    <SectionTitle icon={Utensils}>Food & Dining</SectionTitle>
                    <InfoCard>
                        {/* Food description */}
                        {program?.foodDescription && (
                            <>
                                <Box sx={{ px: cardPx, py: 1.5 }}>
                                    <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1 }}>
                                        Food Description
                                    </Typography>
                                    <Box
                                        sx={{
                                            color: '#475569', fontSize: '0.85rem', lineHeight: 1.65,
                                            '& p': { mb: 1 },
                                            '& ul, & ol': { pl: '1.2rem', mb: 1, '& li': { mb: 0.5 } },
                                            '& *': {
                                                fontFamily: 'inherit !important',
                                                fontSize: 'inherit !important',
                                                color: 'inherit !important',
                                                backgroundColor: 'transparent !important'
                                            },
                                        }}
                                        dangerouslySetInnerHTML={{ __html: program.foodDescription }}
                                    />
                                </Box>
                                {(meals.length > 0 || drinks.length > 0 || dietOptions.length > 0) && (
                                    <Divider />
                                )}
                            </>
                        )}

                        {/* Meals / Drinks / Diet — InfoRow style */}
                        {(meals.length > 0 || drinks.length > 0 || dietOptions.length > 0) && (
                            <Box sx={{ px: cardPx, py: 1.5 }}>
                                {meals.length > 0 && (
                                    <InfoRow label="Meals Provided" noBorder={!drinks.length && !dietOptions.length}>
                                        <ChipGroup
                                            items={meals}
                                            labelKey="mealName"
                                            fallbackKey="ownMeal"
                                            icon={Utensils}
                                            chipColor="#D97706"
                                        />
                                    </InfoRow>
                                )}

                                {drinks.length > 0 && (
                                    <InfoRow label="Drinks" noBorder={!dietOptions.length}>
                                        <ChipGroup
                                            items={drinks}
                                            labelKey="drinkName"
                                            fallbackKey="ownDrink"
                                            icon={Coffee}
                                            chipColor="#0D9488"
                                        />
                                    </InfoRow>
                                )}

                                {dietOptions.length > 0 && (
                                    <InfoRow label="Dietary Options" noBorder>
                                        <ChipGroup
                                            items={dietOptions}
                                            labelKey="dietOptionName"
                                            fallbackKey="ownDietOption"
                                            icon={Apple}
                                            chipColor="#16A34A"
                                        />
                                    </InfoRow>
                                )}
                            </Box>
                        )}
                    </InfoCard>
                </Box>
            )}

        </Box>
    );
};

export default FoodProgramTab;
